import { useLanguage } from '@/contexts/LanguageContext';
import { motion } from 'framer-motion';
import { trackEvent } from '@/lib/analytics';
import LogoVideo from '@/components/LogoVideo';

// Use a stable /hero-bg.webp from /public so the <link rel="preload"> in
// index.html matches the actual <img> src (otherwise Vite hashes the URL
// and the preload is wasted, hurting LCP).
const heroBg = '/hero-bg.webp';

export default function HeroSection() {
  const { t } = useLanguage();

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <img
          src={heroBg}
          alt="Veterinary AI Platform"
          width={1670}
          height={940}
          fetchPriority="high"
          decoding="async"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0" style={{ background: 'var(--gradient-hero)' }} />
      </div>

      <div className="relative z-10 container-max section-padding pt-40 md:pt-48">
        <div className="max-w-3xl">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="inline-block px-3 py-1 rounded-full bg-primary-foreground/10 border border-primary-foreground/20 text-primary-foreground/90 text-xs md:text-sm font-semibold tracking-wide uppercase mb-5 backdrop-blur-sm"
          >
            {t.hero.label}
          </motion.span>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="flex items-center gap-4 md:gap-6 mb-6"
          >
            <LogoVideo
              className="h-24 md:h-32 lg:h-40 w-auto rounded-xl shadow-lg shrink-0"
              ariaLabel="Dr.Vet AI logo"
            />
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-primary-foreground leading-tight">
              {t.hero.headline}
            </h1>
          </motion.div>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.4 }}
            className="text-lg md:text-xl text-primary-foreground/80 leading-relaxed mb-10 max-w-2xl"
          >
            {t.hero.subtitle}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.6 }}
            className="flex flex-col sm:flex-row gap-4"
          >
            <a
              href="#contact"
              onClick={() => trackEvent('hero_cta_click', { page: 'home' })}
              className="btn-cta text-center"
            >
              {t.hero.cta}
            </a>
            <a href="#how-it-works" className="btn-outline-light text-center">
              {t.hero.ctaSecondary}
            </a>
          </motion.div>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.8 }}
            className="mt-6 text-sm md:text-base text-primary-foreground/70 italic"
          >
            {t.hero.proof}
          </motion.p>
        </div>
      </div>
    </section>
  );
}
